import { Box } from "@mui/material";
import { HeaderButton } from "./CustomMUI.js";
import { formatCount, getContentOptionsArray } from "../utils/utils.js";

/**
 * Pill-style content type selector, as used in page headers: All, movies, TV,
 * books — the same order everywhere.
 *
 * `counts` (value → number) appends a muted count after each label that has
 * one.
 *
 * `compact` is for rows that must fit a narrow phone without scrolling: below
 * sm it drops the icons, tightens padding and shares the full row width
 * equally, down to each label's own width. It also shortens "TV shows" to
 * "TV" at every width.
 */
export default function ContentTypePills({
  value,
  onChange,
  disabledValues = [],
  compact = false,
  counts = {},
}) {
  // A fixed order for everyone (All, movies, TV, books): pills are navigation,
  // tapped by position, so they never move
  const options = getContentOptionsArray({ showEverythingOption: true });

  return (
    <Box
      sx={{
        display: "flex",
        gap: compact ? { xs: 0.75, sm: 1 } : 1,
        flexShrink: 0,
        ...(compact && { flexGrow: { xs: 1, sm: 0 } }),
      }}
    >
      {options.map((option, index) => (
        <HeaderButton
          key={option.value}
          startIcon={option.icon}
          onClick={() => onChange(option.value)}
          disabled={disabledValues.includes(option.value)}
          sx={{
            // Breathing room at the end when the row scrolls horizontally
            // (compact rows are sized not to scroll)
            ...(!compact &&
              index === options.length - 1 && { mr: { xs: 2, sm: 0 } }),
            ...(compact && {
              // Equal shares of the row, but never narrower than the label;
              // pills held at their label width leave the remaining space to
              // the others (in practice "All", the shortest label)
              flex: { xs: "1 1 0", sm: "0 0 auto" },
              minWidth: { xs: "max-content", sm: 64 },
              px: { xs: 1.5, sm: 2 },
              "& .MuiButton-startIcon": {
                display: { xs: "none", sm: "inherit" },
              },
            }),
            ...(value === option.value && {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.main" },
            }),
          }}
        >
          {compact && option.value === "tvshow" ? "TV" : option.labelPlural}
          {counts[option.value] != null && (
            <Box component="span" sx={{ ml: 0.75, opacity: 0.7 }}>
              {formatCount(counts[option.value])}
            </Box>
          )}
        </HeaderButton>
      ))}
    </Box>
  );
}
